import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm } from 'antd';
import { routes } from '../../../../../utils/helpers/ApiClient';
import AppBarCMS from '../../../../basic/Navigation/AppBarCMS/AppBarCMS';
import { AdminContentBlock, Spinner } from '../../../../common';
import Form from '../../../../antd/form/Form/Form';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { applyPhdValidationErrors } from '../utils/phdApiFeedback.js';
import {
  useGetPhdStudyFieldsQuery,
  useGetPhdStudySubjectsQuery,
  useSavePhdStudySubjectMutation,
} from '../../../../../redux/services/phd';
import PhdStudySubjectsEditForm from './PhdStudySubjectsEditForm.jsx';
import { usePhdDirtyLeaveGuard } from '../usePhdDirtyLeaveGuard.jsx';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';

const normalizeCollection = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  return [];
};

const formatFieldLabel = (field) => {
  if (!field) {
    return '';
  }

  return field.focus
    ? `${field.study_field} - ${field.focus}`
    : field.study_field;
};

const PhdStudySubjectsEdit = ({ mode }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = AntdForm.useForm();
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const pageTitle = isCreate ? t('Nový predmet') : t('Upraviť predmet');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const { data: subjectsRaw, isLoading: isSubjectsLoading } =
    useGetPhdStudySubjectsQuery();
  const { data: fieldsRaw, isLoading: isFieldsLoading } =
    useGetPhdStudyFieldsQuery();
  const [savePhdStudySubject, { isLoading: isSaving }] =
    useSavePhdStudySubjectMutation();
  const { saveSuccess, saveFailed } = useToastMessages();

  const subjectList = useMemo(
    () => normalizeCollection(subjectsRaw),
    [subjectsRaw],
  );
  const fieldList = useMemo(() => normalizeCollection(fieldsRaw), [fieldsRaw]);

  const detail = useMemo(
    () =>
      isEdit
        ? subjectList.find((item) => String(item.id) === String(id))
        : null,
    [id, isEdit, subjectList],
  );

  const studyFieldOptions = useMemo(
    () =>
      fieldList
        .filter((field) => field?.id != null)
        .map((field) => ({ value: field.id, label: formatFieldLabel(field) }))
        .sort((a, b) => a.label.localeCompare(b.label, 'sk')),
    [fieldList],
  );

  const initials = useMemo(() => {
    if (isCreate) {
      const preselectedFieldId = searchParams.get('fieldId');
      const initialFieldIds =
        preselectedFieldId && !Number.isNaN(Number(preselectedFieldId))
          ? [Number(preselectedFieldId)]
          : [];

      return {
        name: undefined,
        code: undefined,
        subject_type: 'required',
        completion_type: 'S',
        field_ids: initialFieldIds,
      };
    }

    if (!detail) {
      return null;
    }

    return {
      name: detail.name,
      code: detail.code,
      subject_type: detail.subject_type || 'required',
      completion_type: detail.completion_type || 'S',
      field_ids: (detail.fields || []).map((field) => field.id),
    };
  }, [detail, isCreate, searchParams]);

  const { modal, onSaveFailed, onSaveSuccess, onValuesChange } =
    usePhdDirtyLeaveGuard(form, initials);

  const handleSubmit = async (values) => {
    try {
      await savePhdStudySubject({
        id: isEdit ? detail.id : undefined,
        body: {
          name: values.name,
          code: values.code,
          subject_type: values.subject_type,
          completion_type: values.completion_type,
          field_ids: values.field_ids || [],
        },
      }).unwrap();
      saveSuccess();
      onSaveSuccess(() => navigate(routes.cmsPhdStudyFields));
    } catch (error) {
      onSaveFailed();
      applyPhdValidationErrors(form, error);
      saveFailed(error);
    }
  };

  if (
    isFieldsLoading ||
    isSubjectsLoading ||
    (isEdit && !detail) ||
    !initials
  ) {
    return <Spinner />;
  }

  return (
    <>
      {modal}
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsPhdStudyFields}
        backUrlTooltip={t('Študijné odbory a predmety')}
        permissionsBasic="phd-study-subjects"
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako fungujú študijné odbory a predmety?')}
          description={t(
            'Predmet priraďte odborom, v ktorých sa môže používať. Typ predmetu a ukončenia určujú jeho správanie v téme, pláne a hodnotení.',
          )}
        />
        <AdminContentBlock
          icon="fa fa-book"
          title={pageTitle}
          link={routes.cmsPhdStudyFields}
        >
          <Form
            form={form}
            initialValues={initials}
            onFinish={handleSubmit}
            onValuesChange={onValuesChange}
          >
            <PhdStudySubjectsEditForm
              studyFieldOptions={studyFieldOptions}
              isSubmitting={isSaving}
            />
          </Form>
        </AdminContentBlock>
      </div>
    </>
  );
};

export default PhdStudySubjectsEdit;
